export const USER_FACING_FALLBACK =
  "Something went wrong. Please try again later.";

function safeText(value = "") {
  return String(value ?? "").replace(/\s+/g, " ").trim();
}

function containsSensitiveCopy(value = "") {
  const text = safeText(value).toLowerCase();

  return (
    text.includes("secret") ||
    text.includes("authorization") ||
    text.includes("cookie") ||
    text.includes("jwt") ||
    text.includes("bearer ") ||
    text.includes("basic ") ||
    text.includes("api key") ||
    text.includes("client_secret") ||
    text.includes("access_token") ||
    text.includes("refresh_token") ||
    text.includes("mongodb") ||
    text.includes("mongoerror") ||
    text.includes("mongooseerror") ||
    text.includes("sqlstate") ||
    text.includes("database error") ||
    text.includes("stack trace")
  );
}

function looksLikeFilesystemPath(value = "") {
  const text = safeText(value);

  return (
    /(?:^|\s)\/(?:users|home|var|etc|private|app|srv|tmp)\//i.test(text) ||
    /[a-z]:\\(?:users|windows|program files|app|tmp)\\/i.test(text)
  );
}

function looksLikeStackTrace(value = "") {
  const text = safeText(value);

  return /\bat\s+\S+\s+\(.+:\d+:\d+\)/.test(text);
}

function looksLikeDebugCode(value = "") {
  const text = safeText(value);

  return Boolean(text && /^[A-Z0-9_.:-]{8,}$/.test(text));
}

export function getSafeUserMessage(value = "", fallback = "") {
  const message = safeText(value);

  if (!message) return safeText(fallback);
  if (message.length > 300) return safeText(fallback);
  if (containsSensitiveCopy(message)) return safeText(fallback);
  if (looksLikeFilesystemPath(message)) return safeText(fallback);
  if (looksLikeStackTrace(message)) return safeText(fallback);
  if (looksLikeDebugCode(message)) return safeText(fallback);

  return message;
}

function getRetryAfterSeconds(error = {}) {
  const value =
    error?.retryAfterSeconds ||
    error?.response?.data?.retryAfterSeconds ||
    error?.response?.headers?.["retry-after"];

  const seconds = Number(value);

  return Number.isFinite(seconds) && seconds > 0 ? Math.ceil(seconds) : 0;
}

function getStatusMessage(status, error) {
  const retryAfterSeconds = getRetryAfterSeconds(error);

  if (status === 400) {
    return "Please check the information you entered and try again.";
  }

  if (status === 401) {
    return "Your session has expired. Please log in again.";
  }

  if (status === 403) {
    return "You do not have permission to perform this action.";
  }

  if (status === 404) {
    return "We could not find what you requested.";
  }

  if (status === 409) {
    return "This update conflicts with existing information. Refresh the page and try again.";
  }

  if (status === 413) {
    return "The upload is too large. Choose a smaller file and try again.";
  }

  if (status === 415) {
    return "That file type is not supported.";
  }

  if (status === 422) {
    return "Please check the highlighted fields and try again.";
  }

  if (status === 429) {
    if (retryAfterSeconds) {
      return `We've received several attempts. Please wait ${retryAfterSeconds} seconds and try again.`;
    }

    return "We've received several attempts. Please wait a moment and try again.";
  }

  if (status >= 500) {
    return USER_FACING_FALLBACK;
  }

  return "";
}

function normalizeFallbackMessage(value = "") {
  const text = safeText(value);

  if (!text) return USER_FACING_FALLBACK;

  if (/^(something went wrong|request failed)\.?$/i.test(text)) {
    return USER_FACING_FALLBACK;
  }

  const withoutPeriod = text.replace(/\.+$/, "");
  const failedMatch = withoutPeriod.match(
    /^Failed to (fetch|load|save|create|update|delete|send|reserve|validate|refresh|apply|remove|complete|resolve|increase|decrease|clear|merge|restore|move) (.+)$/i,
  );

  if (failedMatch) {
    const operation = failedMatch[1].toLowerCase();
    const target = failedMatch[2].trim();
    const verb = operation === "fetch" ? "load" : operation;

    return `We could not ${verb} ${target} right now. Please try again later.`;
  }

  const unableMatch = withoutPeriod.match(/^Unable to (.+)$/i);

  if (unableMatch) {
    return `We could not ${unableMatch[1].trim()}. Please try again.`;
  }

  return text;
}

export function getApiErrorMessage(
  error,
  { fallback = USER_FACING_FALLBACK } = {},
) {
  const safeFallback = getSafeUserMessage(
    normalizeFallbackMessage(fallback),
    USER_FACING_FALLBACK,
  );
  const responseMessage =
    error?.response?.data?.customerMessage || error?.response?.data?.message;
  const directMessage = error?.normalizedMessage || responseMessage;
  const safeDirectMessage = getSafeUserMessage(directMessage);

  if (safeDirectMessage) {
    return safeDirectMessage;
  }

  if (error?.code === "ECONNABORTED") {
    return "The request timed out. Please try again.";
  }

  if (error?.message === "Network Error" || (error?.request && !error?.response)) {
    return "We could not reach the server. Check your connection and try again.";
  }

  const status = Number(error?.statusCode || error?.response?.status || 0);
  const statusMessage = getStatusMessage(status, error);

  if (statusMessage) {
    return status >= 500 ? USER_FACING_FALLBACK : statusMessage;
  }

  const safeErrorMessage = getSafeUserMessage(error?.message);

  if (safeErrorMessage && safeErrorMessage !== "Network Error") {
    return safeErrorMessage;
  }

  return safeFallback || USER_FACING_FALLBACK;
}

export function buildUserFacingErrorPayload(
  error,
  { fallback = USER_FACING_FALLBACK } = {},
) {
  return {
    message: getApiErrorMessage(error, { fallback }),
    code: safeText(error?.errorCode || error?.response?.data?.code),
    statusCode: Number(error?.statusCode || error?.response?.status || 0),
    requestId: safeText(error?.requestId || error?.response?.data?.requestId),
    retryAfterSeconds: getRetryAfterSeconds(error),
  };
}
